import { CinemaModel, PaymentGatewayModel } from '../cinemas';
import { MyOrderModel, Session } from '../my-booking';

import { ExperienceModel } from '../experiences';
import { Film } from '../movies';
import { OfferModel } from '../offers';

export type SeatType =
  | 'STANDARD'
  | 'PREMIUM'
  | 'BOUTIQUE'
  | 'WHEEL_CHAIR'
  | 'COMPANION'
  | 'SUITES'
  | 'EMPTY';

export type StatusType = 'EXPIRED' | 'PENDING' | 'DONE' | 'CANCELLED';
export type DeviceType = 'IOS' | 'ANDROID' | 'WEBSITE';

export interface OrderModel {
  id: string;
  // id: number;
  userSessionId: string;
  status: StatusType;
  cinema: CinemaModel;
  film: Film;
  experience: ExperienceModel;
  session: Session;
  expireAt: string;
  platform: DeviceType;
  hasReminder: boolean;
  userId: number;
  screen: string;
  selectedTickets?: DetailedSelectedTicketModel[];
  discountAmount: number;
  paymentGateway: PaymentGatewayModel;
  ticketDescription?: string;
}

export type VoucherModel = {
  quantity: number;
  barCode: string;
  discountAmount: number;
};

export interface PaymentDetailsModel {
  total: number;
  quantity: number;
  description: string;
}

export interface ConfirmedOrderModel extends OrderModel {
  vistaBookingId: string;
  bookingFee: number;
  subTotal: number;
  totalCost: number;
  seats: string;
  confirmationDate: string;
  numOfSeats: number;
  vouchers: VoucherModel[];
  paymentDetails: PaymentDetailsModel[];
  offer?: OfferModel;
  showtime: string;
}

export interface BasicTicketModel {
  ticketTypeCode: string;
  price: number;
}

export interface TicketModel extends BasicTicketModel {
  description: string;
}

export interface DetailedTicketModel extends BasicTicketModel {
  areaCategoryCode: string;
}

export interface PositionModel {
  rowIndex: number;
  columnIndex: number;
  areaNumber: number;
}

export interface DetailedPositionModel extends PositionModel {
  columnDisplay: string;
}

export interface SeatModel {
  position: PositionModel;
  isDisabled: boolean;
  seatStyle: SeatType;
}

export interface DetailedSeatModel {
  position: DetailedPositionModel;
  isDisabled: boolean;
  seatStyle: SeatType;
}

export interface SelectedTicketModel extends TicketModel {
  seats: SeatModel[];
}

export interface DetailedSelectedTicketModel extends DetailedTicketModel, TicketModel {
  seats: DetailedSeatModel[];
}

export interface RowModel {
  seats: DetailedSeatModel[];
  physicalName: string;
}

export interface AreaModel {
  rows: RowModel[];
  description: string;
  numberOfSeats: number;
  ticket: DetailedTicketModel;
  rowCount: number;
  columnCount: number;
}

export interface BookingRequest {
  sessionId: number;
  offerId?: string;
  // offerId?: number;
}

export interface CalculateOfferTicketModel {
  ticketTypeCode: string;
  areaCategoryCode: string;
  price: number;
  seatQuantity: number;
  offerQuantity: number;
  description: string;
}

export interface CalculateOfferDetailedTicketModel {
  requestedTicket: CalculateOfferTicketModel;
  offerTicket?: CalculateOfferTicketModel;
}

export interface InitOrderRequest extends BookingRequest {
  clientDate: string;
  bin?: string;
  last4?: string;
  unipalPhoneNumber?: string;
  unipalCode?: string;
  unipalStudentId?: string;
  unipalSessionId?: string;
}

export interface InitOrderResponse {
  experiences: ExperienceModel[];
  order: OrderModel;
  maxSeats: number;
  timer: number;
  offer?: OfferModel;
}

export interface SessionResponse {
  session: {
    id: number;
    showtime: string;
    experience: ExperienceModel;
    film: Film;
    cinema: CinemaModel;
    isThreeD: boolean;
    screen: string;
    ticketDescription?: string;
  };
}

export interface SeatsPlanResponse {
  offerAreaCategories: string[];
  offerTickets: string[];
  seatLayoutData: {
    areas: AreaModel[];
  };
  ticketDescription?: string;
}

export interface ShareSeatsPlanRequest {
  seats: PositionModel[];
}

export interface ShareSeatsPlanResponse {
  url: string;
}

export interface CalculateOfferRequest {
  sessionId: number;
  orderId: string;
  tickets: SelectedTicketModel[];
}

export interface CalculateOfferResponse {
  tickets: CalculateOfferDetailedTicketModel[];
  discountAmount: number;
  totalCost: number; // After discount
  quantity: number;
  package?: string;
}

export interface ConfirmSeatsPlanRequest extends BookingRequest {
  tickets: SelectedTicketModel[];
}

export interface ConfirmSeatsPlanResponse {
  order: ConfirmedOrderModel;
  timer: number;
}

export interface PayOrderResponse {
  message: string;
  order: OrderModel;
  redirectLink: string;
  successLink: string;
}

export interface PayOrderBasicInfo {
  clientDate: string;
  userAgent: string;
  timezone: number;
  screenWidth: number;
  screenHeight: number;
}

export interface PayOrderWithNoAmountRequest {
  paymentStrategy: 'NONE';
  clientDate: string;
}

export interface PayOrderWithWalletRequest {
  paymentStrategy: 'E_WALLET';
  clientDate: string;
}
export interface PayOrderWithCashbackWalletRequest {
  paymentStrategy: 'CASHBACK_WALLET';
  clientDate: string;
}
export interface PayOrderWithApplePayRequest extends PayOrderBasicInfo {
  paymentStrategy: 'TOKEN';
  token: ApplePaymentToken | string;
  payForLimit: boolean; // when offer seats overflow occurred | error code 106
  clientDate: string;
  // guestUserId?: number;
  phoneNumber?: string;
  appleDisplayName?: string; // for PayFort
  appleNetwork?: string; // for PayFort
  appleType?: string; // for PayFort
  isApplePayment: boolean; // for PayFort
}

export interface PayOrderWithAmountRequest extends PayOrderBasicInfo {
  paymentStrategy: 'CARD';
  cardId: number;
  cvv: string;
  payForLimit: boolean; // when offer seats overflow occurred | error code 106
  eWalletAmount?: number;
  cashbackWalletAmount?: number;
}

export type PayOrderWithTabbyPayRequest = {
  paymentStrategy: 'TABBY';
  clientDate: string;
};

// STEP 1: mirrors Tabby — Tamara-specific params land here in step 2.
export type PayOrderWithTamaraPayRequest = {
  paymentStrategy: 'TAMARA';
  clientDate: string;
};

export type StcPayRequest = {
  paymentStrategy: 'STC_PAY';
  checkoutPaymentContext: string;
  otp: string;
};

export type PayOrderWithStcPayRequest = Pick<
  PayOrderBasicInfo,
  'timezone' | 'userAgent' | 'clientDate'
> &
  StcPayRequest & { eWalletAmount?: number; cashbackWalletAmount?: number };

export type PayOrderRequest =
  | PayOrderWithNoAmountRequest
  | PayOrderWithAmountRequest
  | PayOrderWithWalletRequest
  | PayOrderWithCashbackWalletRequest
  | PayOrderWithApplePayRequest
  | PayOrderWithStcPayRequest
  | PayOrderWithTabbyPayRequest
  | PayOrderWithTamaraPayRequest;

export interface PayOrderGuestBookingInfo {
  phoneNumber: string;
  firstName: string;
  lastName: string;
  email: string;
  expiryMonth?: string;
  expiryYear?: string;
}

export interface PayOrderFullGuestBookingInfo
  extends PayOrderBasicInfo,
    PayOrderGuestBookingInfo {}

export interface PayOrderGuestBookingWithNoAmountRequest
  extends PayOrderFullGuestBookingInfo {
  paymentStrategy: 'NONE';
}

export interface PayOrderGuestBookingWithAmountRequest
  extends PayOrderFullGuestBookingInfo {
  paymentStrategy: 'TOKEN';
  isApplePayment?: boolean;
  token: string | ApplePaymentToken;
  payForLimit: boolean; // when offer seats overflow occurred | error code 106
  bin?: string; // when bank offer applied
  expiryMonth?: string;
  expiryYear?: string;
  cardBin?: string; // when payment gateway is checkout
  cardLast4?: string; // when payment gateway is checkout
  cvv?: string; // when payment gateway is hyper pay
  appleDisplayName?: string; // for PayFort
  appleNetwork?: string; // for PayFort
  appleType?: string; // for PayFort
}
export type PayOrderGuestBookingWithStcPayRequest = PayOrderGuestBookingInfo &
  StcPayRequest &
  PayOrderBasicInfo & { payForLimit: boolean };

export type PayOrderGuestBookingRequest =
  | PayOrderGuestBookingWithNoAmountRequest
  | PayOrderGuestBookingWithAmountRequest
  | PayOrderGuestBookingWithStcPayRequest
  | PayOrderWithTabbyPayRequest
  | PayOrderWithTamaraPayRequest;

export enum REDIRECT_TYPE {
  URL = 'URL',
  HTML = 'HTML',
}

export interface PayOrderResponse {
  message: string;
  order: OrderModel;
  redirect: string;
  redirectType: REDIRECT_TYPE;
  successLink: string;
  /** Present when payment succeeded but Vista confirmation is still pending (code 26). */
  code?: number;
}

export interface GetOrderByIdResponse {
  order: MyOrderModel;
}

export interface ValidateAppleSessionResponse {
  merchantSession: string;
}

export interface ValidateAppleSessionRequest {
  appleUrl?: string;
  paymentGateway: string;
}
export interface CreateAppleSessionResponse {
  // token: string;
  token: ApplePaymentToken;
}

export interface CreateAppleSessionRequest {
  version: string;
  data: string;
  signature: string;
  transactionId: string;
  publicKeyHash: string;
  ephemeralPublicKey: string;
}

export interface AppleTokenPaymentMethod {
  displayName: string;
  network: string;
  type: string;
}

export interface AppleToken {
  paymentData: {
    data: string;
    header: {
      ephemeralPublicKey: string;
      publicKeyHash: string;
      transactionId: string;
    };
    signature: string;
    version: string;
  };
  paymentMethod: AppleTokenPaymentMethod;
  transactionIdentifier: string;
}
export interface ApplePaymentToken {
  token: AppleToken;
}

export interface AppleSessionEvent {
  payment: ApplePaymentToken;
  type: string; // eg. 'paymentauthorized'
}

export interface OrderSubmitted {
  id: number;
  isSubmitted: boolean;
}
